import { z } from "zod";

export const BRIDGE_URL = "https://nbhjcrhsjyfcsahcipqs.supabase.co";
export const BRIDGE_KEY = "sb_publishable_KlwwxefC89F5txLZUYVRog_ZjwS0HRx";
export const navSchema = z.object({
  command_id: z.string().min(1).max(100), issued_at: z.string().datetime(),
  target: z.enum(["industry", "client", "contact", "follow_up", "reminder", "task", "prospect", "trade_show", "campaign", "module", "pipeline", "map", "planning"]),
  id: z.string().min(1).max(100).optional(), industry: z.string().min(1).max(200).optional(),
  module: z.string().min(1).max(100).optional(), label: z.string().max(200).optional(),
});
export type NavCommand = z.infer<typeof navSchema>;
export const moduleRoutes: Record<string, string> = {
  dashboard: "/index", clients: "/index", contacts: "/contacts", pipeline: "/stages", stages: "/stages",
  "follow-ups": "/index", follow_ups: "/index", reminders: "/index", tasks: "/index", todos: "/index",
  "weekly log": "/weeks", weekly_log: "/weeks", weeks: "/weeks", prospecting: "/prospecting", prospects: "/prospecting",
  "trade-shows": "/trade-shows", trade_shows: "/trade-shows", "trade shows": "/trade-shows",
  campaigns: "/communicator", communicator: "/communicator", imports: "/contacts", reports: "/reports",
  map: "/mapping", mapping: "/mapping", planning: "/planning",
};
export const recordTables = {
  client: "clients", contact: "company_contacts", follow_up: "follow_ups", reminder: "reminds",
  task: "todos", prospect: "prospects", trade_show: "trade_shows", campaign: "campaigns",
} as const;
export const MAX_CLOCK_SKEW_MS = 120_000;
export function acceptCommand(payload: unknown, follow: boolean, visible: boolean, seen: Set<string>, now = Date.now()): NavCommand | null {
  const result = navSchema.safeParse(payload);
  if (!follow || !visible || !result.success) return null;
  const cmd = result.data;
  const age = now - Date.parse(cmd.issued_at);
  // Allow modest clock differences between the Bridge server and this computer.
  if (age < -MAX_CLOCK_SKEW_MS || age > 60_000 + MAX_CLOCK_SKEW_MS || seen.has(cmd.command_id)) return null;
  if (cmd.target === "industry" && !cmd.industry) return null;
  if (cmd.target in recordTables && !cmd.id) return null;
  if (cmd.target === "module" && !moduleRoutes[cmd.module?.trim().toLowerCase() ?? ""]) return null;
  seen.add(cmd.command_id);
  if (seen.size > 200) { const oldest = seen.values().next().value; if (oldest) seen.delete(oldest); }
  return cmd;
}
export function commandRoute(cmd: NavCommand): string {
  if (cmd.target === "module") return moduleRoutes[cmd.module?.trim().toLowerCase() ?? ""];
  if (cmd.target === "pipeline") return "/stages";
  if (cmd.target === "map") return "/mapping";
  if (cmd.target === "planning") return "/planning";
  if (cmd.target === "prospect") return "/prospecting";
  if (cmd.target === "trade_show") return "/trade-shows";
  if (cmd.target === "campaign") return "/communicator";
  return "/index";
}
export function refreshTables(table: string): string[] {
  if (["clients", "company_contacts", "client_industries", "client_stages", "industries"].includes(table)) return [table, "clients", "company_contacts", "client_stages", "industries", "reminds"];
  if (["follow_ups", "reminds"].includes(table)) return ["follow_ups", "reminds", "clients"];
  return [table];
}
/** Tables whose saved changes should bring the matching record on screen. */
export const refreshTargets: Record<string, NavCommand["target"]> = {
  clients: "client", company_contacts: "contact", follow_ups: "follow_up", reminds: "reminder", todos: "task",
  prospects: "prospect", trade_shows: "trade_show", campaigns: "campaign", client_stages: "pipeline",
};
/** What this browser is currently showing, reported so ChatGPT knows where the owner is working. */
export type ViewState = { path: string; industry?: string; client?: { id: number | string; name?: string } };
export function buildView(pathname: string, industry: string | null | undefined, client: { id: number | string; name?: string } | null | undefined): ViewState {
  const view: ViewState = { path: pathname };
  const name = industry?.trim();
  if (name) view.industry = name;
  if (client && client.id !== undefined && client.id !== null && client.id !== "") {
    view.client = { id: client.id, ...(client.name ? { name: String(client.name).slice(0, 200) } : {}) };
  }
  return view;
}

export type ConnectionLevel = "red" | "yellow" | "green";
/** How long a verified exchange with the Bridge stays "recent". */
export const ACTIVITY_FRESH_MS = 5 * 60_000;
/** How fresh a reported view must be for ChatGPT to treat the session as active. */
export const SESSION_ACTIVE_MS = 90_000;
export function connectionStatus(opts: { paired: boolean; follow: boolean; channelState: string; lastActivityAt: number | null; now?: number }): ConnectionLevel {
  if (!opts.paired || !opts.follow) return "red";
  if (opts.channelState !== "SUBSCRIBED") {
    return ["CHANNEL_ERROR", "TIMED_OUT", "CLOSED"].includes(opts.channelState) ? "red" : "yellow";
  }
  const now = opts.now ?? Date.now();
  return opts.lastActivityAt !== null && now - opts.lastActivityAt <= ACTIVITY_FRESH_MS ? "green" : "yellow";
}

/** Turns a ChatGPT save notification into a read-only "show it" command. */
export function commandFromRefresh(payload: unknown, now = Date.now()): NavCommand | null {
  if (!payload || typeof payload !== "object") return null;
  const { table, id, at } = payload as Record<string, unknown>;
  if (typeof table !== "string") return null;
  const target = refreshTargets[table];
  if (!target) return null;
  const recordId = typeof id === "string" || typeof id === "number" ? String(id) : undefined;
  if (target in recordTables && !recordId) return null;
  const issued = typeof at === "string" && !Number.isNaN(Date.parse(at)) ? new Date(at).toISOString() : new Date(now).toISOString();
  const parsed = navSchema.safeParse({ command_id: `save-${table}-${recordId ?? "all"}-${issued}`.slice(0, 100), issued_at: issued, target, ...(target in recordTables ? { id: recordId } : {}) });
  return parsed.success ? parsed.data : null;
}
